import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Home, FileText } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentData(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (response.data.status === "expired") {
          setStatus("error");
        } else if (attempts < 5) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error(error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-white to-secondary/50 flex items-center justify-center py-12 px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full"
      >
        <div className="glass-card bg-white/95 shadow-2xl text-center">
          {/* Loading State */}
          {status === "loading" && (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-8"
              >
                <Loader2 className="text-primary" size={40} />
              </motion.div>
              <h1 className="font-outfit font-bold text-2xl text-text-primary mb-4">
                Vérification du paiement...
              </h1>
              <p className="font-manrope text-text-secondary">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </>
          )}

          {/* Success State */}
          {status === "success" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8"
              >
                <CheckCircle className="text-green-600" size={40} />
              </motion.div>
              <h1 className="font-outfit font-bold text-2xl text-text-primary mb-4">
                Paiement réussi !
              </h1>
              <p className="font-manrope text-text-secondary mb-6">
                Merci pour votre achat. Votre pack a été activé avec succès.
              </p>
              {paymentData && (
                <div className="bg-secondary/50 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm text-text-secondary font-manrope">
                    <span className="font-semibold">Pack:</span> {paymentData.metadata?.pack_name || "Pack"}
                  </p>
                  <p className="text-sm text-text-secondary font-manrope">
                    <span className="font-semibold">Montant:</span> {(paymentData.amount_total / 100).toFixed(2)} €
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto" data-testid="btn-home">
                    <Home size={18} className="mr-2" />
                    Accueil
                  </Button>
                </Link>
                <Link to="/simulation">
                  <Button className="btn-primary w-full sm:w-auto" data-testid="btn-simulation">
                    <FileText size={18} className="mr-2" />
                    Lancer une simulation
                  </Button>
                </Link>
              </div>
            </>
          )}

          {/* Pending State */}
          {status === "pending" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-8"
              >
                <Loader2 className="text-yellow-600" size={40} />
              </motion.div>
              <h1 className="font-outfit font-bold text-2xl text-text-primary mb-4">
                Paiement en cours de traitement
              </h1>
              <p className="font-manrope text-text-secondary mb-6">
                Votre paiement est en cours de traitement. Vous recevrez une confirmation par email.
              </p>
              <Link to="/">
                <Button className="btn-primary" data-testid="btn-home-pending">
                  Retour à l'accueil
                </Button>
              </Link>
            </>
          )}

          {/* Error State */}
          {status === "error" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-8"
              >
                <XCircle className="text-red-600" size={40} />
              </motion.div>
              <h1 className="font-outfit font-bold text-2xl text-text-primary mb-4">
                Problème avec le paiement
              </h1>
              <p className="font-manrope text-text-secondary mb-6">
                Une erreur est survenue lors du traitement de votre paiement. 
                Veuillez réessayer ou contacter notre support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/tarifs">
                  <Button className="btn-primary w-full sm:w-auto" data-testid="btn-retry">
                    Réessayer
                  </Button>
                </Link>
                <a href="mailto:contact@eco-conseil-habitat.fr">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto">
                    Contacter le support
                  </Button>
                </a>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
